import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import useFetch from "../../../hooks/useFetch";

import { useTinyThinkGame } from "./hooks/useTinyThinkGame";
import { useTinyThinkSound } from "./utils/useTinyThinkSound";
import ConsentGate from "./components/ConsentGate";
import TinyThinkHome from "./TinyThinkHome";
import TinyThinkGame from "./TinyThinkGame";
import Gumball from "../gumball/Gumball";

import "./tinythink.css";

export default function TinyThink({ authUser }) {
  const authState = useSelector((state) => state.authReducer || {});
  const userId = authState?.user?._id || null;
  const userCode = authState?.user?.think5whyscode || "";
  const userName = authState?.user?.name || "User";
  const [showGumball, setShowGumball] = useState(false);
  
  const [consentChecked, setConsentChecked] = useState(false);
  const [fetchData] = useFetch();
  const [consentLoading, setConsentLoading] = useState(true);
  const [hasConsent, setHasConsent] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const sound = useTinyThinkSound(soundEnabled);
  const game = useTinyThinkGame({ userId, userCode, userName });

  useEffect(() => {
    if (!userId) {
      setConsentLoading(false);
      return;
    }

    let cancelled = false;
    setConsentLoading(true);

    fetchData({
      url: "/tinythink/consent/check",
      method: "post",
      data: { userId },
      headers: { Authorization: authState.token },
    })
      .then((response) => {
        if (cancelled) return;
        setHasConsent(!!response?.data?.consentGiven);
      })
      .catch(() => {
        if (!cancelled) setHasConsent(false);
      })
      .finally(() => {
        if (!cancelled) setConsentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleConsentGiven = () => {
    setHasConsent(true);
  };

  if (!userId) {
    return (
      <div className="tinythink-root min-h-screen flex items-center justify-center bg-[#140346] text-sm text-[#e5e4e9]">
        Please sign in to play PurpleDoor TinyThink. {userId}
      </div>
    );
  }

  if (consentLoading) {
    return (
      <div className="tinythink-root min-h-screen flex items-center justify-center bg-[#140346] text-sm text-[#e5e4e9]">
        Loading...
      </div>
    );
  }

  if (!hasConsent) {
    return (
      <div className="tinythink-root">
        <ConsentGate userId={userId} onConsentGiven={handleConsentGiven} />
      </div>
    );
  }

  if (game.phase === "home") {
    return (
      <div className="tinythink-root">
        <TinyThinkHome
          theme={game.theme}
          streakInfo={game.streakInfo}
          history={game.history}
          bestRecord={game.bestRecord}
          leaderboard={game.leaderboard}
          fetchLeaderboard={game.fetchLeaderboard}
          loading={game.loading}
          todayCompleted={game.todayCompleted}
          onStart={() => game.startSession()}
          soundEnabled={soundEnabled}
          onToggleSound={() => setSoundEnabled((s) => !s)}
        />
      </div>
    );
  }

  return (
    <div className="tinythink-root">
      <TinyThinkGame
        theme={game.theme}
        currentLevel={game.currentLevel}
        phase={game.phase}
        questions={game.questions}
        loadingQ={game.loadingQuestions}
        qError={game.questionsError}
        todayCompleted={game.todayCompleted}
        lastLevelResult={game.lastLevelResult}
        surpriseBox={game.surpriseBox}
        sessionElapsedSec={game.sessionElapsedSec}
        showTimeNudge={game.showTimeNudge}
        dismissTimeNudge={game.dismissTimeNudge}
        submitLevelResult={game.submitLevelResult}
        fetchQuestions={game.fetchQuestions}
        goToNextLevel={game.goToNextLevel}
        returnHome={game.returnHome}
        sound={sound}
      />
      {showGumball && (
        <Gumball
          history={game.history}
          streakInfo={game.streakInfo}
          onClose={() => setShowGumball(false)}
        />
      )}
    </div>
  );
}
